import React, { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../../lib/supabase';

// ─── Config ────────────────────────────────────────────────────────────────
const CHAT_API = '/api/chat';

function genSessionId() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function getSessionId() {
  let sid = localStorage.getItem('qg_chat_session');
  if (!sid) { sid = genSessionId(); localStorage.setItem('qg_chat_session', sid); }
  return sid;
}

// ─── TypingDots ─────────────────────────────────────────────────────────────
function TypingDots() {
  return (
    <div style={styles.typingDots}>
      <span style={{ ...styles.dot, animationDelay: '0s' }} />
      <span style={{ ...styles.dot, animationDelay: '0.2s' }} />
      <span style={{ ...styles.dot, animationDelay: '0.4s' }} />
    </div>
  );
}

// ─── Intake Gate (name + email form) ────────────────────────────────────────
function IntakeGate({ onSubmit }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimName = name.trim();
    const trimEmail = email.trim();
    if (!trimName) { setError('Please enter your name.'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimEmail)) { setError('Please enter a valid email.'); return; }
    setError('');
    setSubmitting(true);
    onSubmit({ name: trimName, email: trimEmail });
  };

  return (
    <div style={intakeStyles.wrap}>
      <div style={intakeStyles.icon}>👋</div>
      <div style={intakeStyles.heading}>Before we chat…</div>
      <div style={intakeStyles.sub}>Tell us who you are so we can personalize your experience.</div>
      <form onSubmit={handleSubmit} style={intakeStyles.form}>
        <input
          type="text"
          placeholder="Your name"
          value={name}
          onChange={e => setName(e.target.value)}
          style={intakeStyles.input}
          autoFocus
        />
        <input
          type="email"
          placeholder="Your email address"
          value={email}
          onChange={e => setEmail(e.target.value)}
          style={intakeStyles.input}
        />
        {error && <div style={intakeStyles.error}>{error}</div>}
        <button type="submit" disabled={submitting} style={intakeStyles.btn}>
          {submitting ? 'Starting…' : 'Start Chat →'}
        </button>
      </form>
      <div style={intakeStyles.privacy}>🔒 We'll never spam you or share your info.</div>
    </div>
  );
}

const intakeStyles = {
  wrap: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '28px 24px',
    gap: 10,
  },
  icon: {
    fontSize: 40,
    marginBottom: 4,
  },
  heading: {
    color: 'white',
    fontWeight: 700,
    fontSize: 18,
    textAlign: 'center',
  },
  sub: {
    color: 'rgba(255,255,255,0.55)',
    fontSize: 13,
    textAlign: 'center',
    marginBottom: 8,
    lineHeight: 1.5,
  },
  form: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  input: {
    width: '100%',
    background: 'rgba(255,255,255,0.07)',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: 10,
    padding: '11px 14px',
    color: 'white',
    fontSize: 14,
    outline: 'none',
    boxSizing: 'border-box',
  },
  error: {
    color: '#f87171',
    fontSize: 12,
    textAlign: 'center',
  },
  btn: {
    width: '100%',
    padding: '12px 0',
    background: 'linear-gradient(135deg, #eab308, #ca8a04)',
    border: 'none',
    borderRadius: 10,
    color: '#000',
    fontWeight: 700,
    fontSize: 14,
    cursor: 'pointer',
    marginTop: 4,
  },
  privacy: {
    color: 'rgba(255,255,255,0.3)',
    fontSize: 11,
    marginTop: 4,
    textAlign: 'center',
  },
};

// ─── Message bubble ──────────────────────────────────────────────────────────
function Bubble({ msg, isNew }) {
  const isUser = msg.role === 'user';
  const isHuman = msg.role === 'human';
  return (
    <div
      style={{
        ...styles.bubbleWrap,
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        animation: isNew ? 'qgSlideIn 0.3s ease' : 'none',
      }}
    >
      {!isUser && (
        <div style={styles.avatar}>
          {isHuman ? '👤' : '🤖'}
        </div>
      )}
      <div
        style={{
          ...styles.bubble,
          ...(isUser ? styles.bubbleUser : isHuman ? styles.bubbleHuman : styles.bubbleAI),
        }}
      >
        {!isUser && (
          <div style={styles.senderLabel}>
            {isHuman ? '💬 QuickGrowth Team' : '🤖 QG Assistant'}
          </div>
        )}
        <div style={styles.bubbleText}>{msg.text}</div>
        <div style={styles.bubbleTime}>{msg.time}</div>
      </div>
    </div>
  );
}

// ─── Main widget ─────────────────────────────────────────────────────────────
export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [notifCount, setNotifCount] = useState(0);
  const [humanMode, setHumanMode] = useState(false);
  const [newMsgIds, setNewMsgIds] = useState(new Set());
  const [sessionId] = useState(getSessionId);
  const [buttonPulse, setButtonPulse] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 640);
  const [userInfo, setUserInfo] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`qg_user_${getSessionId()}`) || 'null'); } catch { return null; }
  });
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const handleIntakeSubmit = useCallback(({ name, email }) => {
    const info = { name, email };
    localStorage.setItem(`qg_user_${sessionId}`, JSON.stringify(info));
    setUserInfo(info);
    // Personalised welcome message now that we know their name
    const welcome = {
      id: 'welcome',
      role: 'ai',
      text: `👋 Hey ${name}! I'm Cody, your QuickGrowth consultant. I can help you with:\n\n• Website design & development\n• Digital marketing & SEO\n• Review generation\n• Social media management\n\nWhat can I help you with today?`,
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    };
    setMessages([welcome]);
  }, [sessionId]);

  // Init: show welcome message (only if user already passed intake)
  useEffect(() => {
    if (!userInfo) return; // don't load messages until intake is done
    const stored = localStorage.getItem(`qg_msgs_${sessionId}`);
    if (stored) {
      try { setMessages(JSON.parse(stored)); } catch {}
    } else {
      const welcome = {
        id: 'welcome',
        role: 'ai',
        text: `👋 Hey ${userInfo.name}! I'm Cody, your QuickGrowth consultant. I can help you with:\n\n• Website design & development\n• Digital marketing & SEO\n• Review generation\n• Social media management\n\nWhat can I help you with today?`,
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      };
      setMessages([welcome]);
    }
  }, [sessionId, userInfo]);

  // Save messages to localStorage
  useEffect(() => {
    if (messages.length > 0) {
      localStorage.setItem(`qg_msgs_${sessionId}`, JSON.stringify(messages.slice(-50)));
    }
  }, [messages, sessionId]);

  // Scroll to bottom when messages change
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  // Supabase realtime — subscribe to human agent replies (live handoff)
  useEffect(() => {
    const channel = supabase
      .channel(`chat:${sessionId}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'admin_notes',
        filter: `note=like.%${sessionId}%`,
      }, (payload) => {
        const note = payload.new;
        const match = (note.note || '').match(/\[CHAT_REPLY\]\s*(.*)/s);
        if (match) {
          addMessage({ role: 'human', text: match[1].trim() });
          setHumanMode(true);
        }
      })
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Focus input when chat opens
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 300);
      setNotifCount(0);
    }
  }, [open]);

  const addMessage = useCallback((msg) => {
    const id = `msg_${Date.now()}_${Math.random()}`;
    const full = { ...msg, id, time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) };
    setMessages(prev => [...prev, full]);
    setNewMsgIds(prev => new Set([...prev, id]));
    setTimeout(() => setNewMsgIds(prev => { const n = new Set(prev); n.delete(id); return n; }), 1000);

    if (msg.role !== 'user') {
      // Trigger notification
      if (!open) {
        setNotifCount(c => c + 1);
        setButtonPulse(true);
        setTimeout(() => setButtonPulse(false), 2000);
      }
      // Play subtle sound
      try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain); gain.connect(ctx.destination);
        osc.frequency.setValueAtTime(880, ctx.currentTime);
        osc.frequency.setValueAtTime(1100, ctx.currentTime + 0.1);
        gain.gain.setValueAtTime(0.15, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
        osc.start(); osc.stop(ctx.currentTime + 0.3);
      } catch {}
    }
  }, [open]);

  const sendMessage = useCallback(async (overrideText) => {
    const text = (overrideText || input).trim();
    if (!text || loading) return;
    setInput('');
    addMessage({ role: 'user', text });
    setLoading(true);

    try {
      const res = await fetch(CHAT_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          message: text,
          user_name: userInfo?.name || null,
          user_email: userInfo?.email || null,
        }),
      });

      if (!res.ok) throw new Error('Network error');
      const data = await res.json();

      if (data.reply) {
        addMessage({ role: 'ai', text: data.reply });
      }
    } catch {
      addMessage({
        role: 'ai',
        text: "Sorry, I'm having a bit of trouble right now. Please try again in a moment, or reach out to us directly!",
      });
    } finally {
      setLoading(false);
    }
  }, [input, loading, sessionId, userInfo, addMessage]);

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const clearChat = () => {
    localStorage.removeItem(`qg_msgs_${sessionId}`);
    localStorage.removeItem(`qg_user_${sessionId}`);
    localStorage.removeItem('qg_chat_session');
    window.location.reload();
  };

  return (
    <>
      {/* Inject keyframes */}
      <style>{`
        @keyframes qgSlideIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes qgPulse { 0%,100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(234,179,8,0.7); } 50% { transform: scale(1.08); box-shadow: 0 0 0 12px rgba(234,179,8,0); } }
        @keyframes qgBounce { 0%,100%{transform:translateY(0);} 50%{transform:translateY(-4px);} }
        @keyframes qgFadeIn { from{opacity:0;transform:scale(0.95) translateY(10px);} to{opacity:1;transform:scale(1) translateY(0);} }
        @keyframes qgSlideUp { from{opacity:0;transform:translateY(100%);} to{opacity:1;transform:translateY(0);} }
        @keyframes qgDot { 0%,60%,100%{transform:translateY(0);opacity:0.4;} 30%{transform:translateY(-6px);opacity:1;} }
        .qg-input::placeholder{color:rgba(255,255,255,0.4);}
        .qg-input:focus{outline:none;}
        .qg-scroll::-webkit-scrollbar{width:4px;}
        .qg-scroll::-webkit-scrollbar-track{background:rgba(255,255,255,0.05);}
        .qg-scroll::-webkit-scrollbar-thumb{background:rgba(255,255,255,0.2);border-radius:4px;}
        .qg-btn:hover{background:#ca8a04!important;}
        .qg-clear:hover{opacity:1!important;}
        .qg-window-mobile{position:fixed;left:0!important;right:0!important;bottom:0!important;width:100%!important;max-width:100%!important;border-radius:20px 20px 0 0!important;max-height:88dvh!important;animation:qgSlideUp 0.3s ease!important;}
        .qg-fab-mobile{bottom:16px!important;right:16px!important;width:54px!important;height:54px!important;}
      `}</style>

      {/* Floating button — hidden on mobile when chat is open (header close btn handles it) */}
      {!(isMobile && open) && (
        <button
          onClick={() => { setOpen(o => !o); setNotifCount(0); }}
          className={isMobile ? 'qg-fab-mobile' : ''}
          style={{
            ...styles.fab,
            animation: buttonPulse ? 'qgPulse 0.6s ease' : 'none',
          }}
          aria-label="Open chat"
        >
          {open ? (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          ) : (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
              <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z"/>
            </svg>
          )}
          {notifCount > 0 && !open && (
            <span style={styles.badge}>{notifCount}</span>
          )}
        </button>
      )}

      {/* Mobile backdrop */}
      {open && isMobile && (
        <div
          onClick={() => setOpen(false)}
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 9997,
            animation: 'qgFadeIn 0.2s ease',
          }}
        />
      )}

      {/* Chat window */}
      {open && (
        <div className={isMobile ? 'qg-window-mobile' : ''} style={styles.window}>
          {/* Header */}
          <div style={styles.header}>
            <div style={styles.headerLeft}>
              <div style={styles.headerAvatar}>⚡</div>
              <div>
                <div style={styles.headerTitle}>QuickGrowth AI</div>
                <div style={styles.headerSub}>
                  {humanMode ? '👤 Human agent active' : '🟢 Online · Typically replies instantly'}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              {isMobile && (
                <button
                  onClick={() => setOpen(false)}
                  className="qg-clear"
                  style={styles.clearBtn}
                  title="Close chat"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                </button>
              )}
              <button onClick={clearChat} className="qg-clear" style={styles.clearBtn} title="Start new chat">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-3.51"/>
                </svg>
              </button>
            </div>
          </div>

          {/* Show intake gate OR chat depending on whether user has identified */}
          {!userInfo ? (
            <IntakeGate onSubmit={handleIntakeSubmit} />
          ) : (
            <>
              {humanMode && (
                <div style={styles.humanBanner}>
                  👤 A human agent has joined this conversation
                </div>
              )}

              {/* Messages */}
              <div className="qg-scroll" style={styles.messages}>
                {messages.map(msg => (
                  <Bubble key={msg.id} msg={msg} isNew={newMsgIds.has(msg.id)} />
                ))}
                {loading && (
                  <div style={{ ...styles.bubbleWrap, justifyContent: 'flex-start' }}>
                    <div style={styles.avatar}>🤖</div>
                    <div style={{ ...styles.bubble, ...styles.bubbleAI }}>
                      <TypingDots />
                    </div>
                  </div>
                )}
                <div ref={bottomRef} />
              </div>

              {/* Input */}
              <div style={styles.inputArea}>
                <textarea
                  ref={inputRef}
                  className="qg-input"
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={handleKey}
                  placeholder="Type your message…"
                  rows={1}
                  style={{ ...styles.input, fontSize: isMobile ? 16 : 14 }}
                  disabled={loading}
                />
                <button
                  onClick={sendMessage}
                  disabled={!input.trim() || loading}
                  className="qg-btn"
                  style={{
                    ...styles.sendBtn,
                    width: isMobile ? 48 : 42,
                    height: isMobile ? 48 : 42,
                    opacity: (!input.trim() || loading) ? 0.5 : 1,
                    cursor: (!input.trim() || loading) ? 'not-allowed' : 'pointer',
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
                    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                  </svg>
                </button>
              </div>

              {!humanMode && (
                <div style={{ textAlign: 'center', paddingBottom: 6 }}>
                  <button
                    onClick={() => sendMessage('I would like to speak with a real person.')}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: 'rgba(234,179,8,0.7)', fontSize: 11, textDecoration: 'underline',
                      padding: '2px 4px',
                    }}
                  >
                    Talk to a real person →
                  </button>
                </div>
              )}
            </>
          )}

          <div style={styles.footer}>
            Powered by <strong>QuickGrowth AI</strong> · <a href="/contact" style={{ color: '#eab308', textDecoration: 'none' }}>Contact us</a>
          </div>
        </div>
      )}
    </>
  );
}

// ─── Styles ──────────────────────────────────────────────────────────────────
const styles = {
  fab: {
    position: 'fixed',
    bottom: 28,
    right: 28,
    width: 60,
    height: 60,
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #eab308, #ca8a04)',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 4px 24px rgba(234,179,8,0.5)',
    zIndex: 9999,
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  },
  badge: {
    position: 'absolute',
    top: -4,
    right: -4,
    background: '#ef4444',
    color: 'white',
    fontSize: 11,
    fontWeight: 700,
    width: 20,
    height: 20,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '2px solid #111',
    animation: 'qgBounce 1s ease infinite',
  },
  window: {
    position: 'fixed',
    bottom: 104,
    right: 28,
    width: 380,
    maxHeight: 580,
    background: 'linear-gradient(180deg, #1a1a2e 0%, #16213e 100%)',
    borderRadius: 20,
    boxShadow: '0 20px 60px rgba(0,0,0,0.6), 0 0 0 1px rgba(234,179,8,0.2)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    zIndex: 9998,
    animation: 'qgFadeIn 0.25s ease',
    fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  header: {
    padding: '16px 20px',
    background: 'linear-gradient(135deg, #1e1b4b, #312e81)',
    borderBottom: '1px solid rgba(234,179,8,0.15)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  headerAvatar: {
    width: 40,
    height: 40,
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #eab308, #ca8a04)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 18,
    flexShrink: 0,
  },
  headerTitle: {
    color: 'white',
    fontWeight: 700,
    fontSize: 15,
  },
  headerSub: {
    color: 'rgba(255,255,255,0.6)',
    fontSize: 11,
    marginTop: 2,
  },
  clearBtn: {
    background: 'none',
    border: 'none',
    color: 'rgba(255,255,255,0.4)',
    cursor: 'pointer',
    padding: 6,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    opacity: 0.6,
    transition: 'opacity 0.2s',
  },
  humanBanner: {
    background: 'rgba(16,185,129,0.15)',
    borderBottom: '1px solid rgba(16,185,129,0.3)',
    color: '#6ee7b7',
    fontSize: 12,
    padding: '8px 16px',
    textAlign: 'center',
    fontWeight: 500,
  },
  messages: {
    flex: 1,
    overflowY: 'auto',
    padding: '16px 14px',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  bubbleWrap: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: 8,
  },
  avatar: {
    width: 28,
    height: 28,
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.08)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 14,
    flexShrink: 0,
  },
  bubble: {
    maxWidth: '80%',
    padding: '10px 14px',
    borderRadius: 16,
    fontSize: 13.5,
    lineHeight: 1.5,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  bubbleUser: {
    background: 'linear-gradient(135deg, #eab308, #ca8a04)',
    color: '#000',
    borderBottomRightRadius: 4,
    fontWeight: 500,
  },
  bubbleAI: {
    background: 'rgba(255,255,255,0.07)',
    color: 'rgba(255,255,255,0.9)',
    borderBottomLeftRadius: 4,
    border: '1px solid rgba(255,255,255,0.08)',
  },
  bubbleHuman: {
    background: 'rgba(16,185,129,0.12)',
    color: '#d1fae5',
    borderBottomLeftRadius: 4,
    border: '1px solid rgba(16,185,129,0.25)',
  },
  senderLabel: {
    fontSize: 10,
    fontWeight: 700,
    opacity: 0.6,
    marginBottom: 4,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  bubbleText: {
    lineHeight: 1.6,
  },
  bubbleTime: {
    fontSize: 10,
    opacity: 0.45,
    marginTop: 6,
    textAlign: 'right',
  },
  typingDots: {
    display: 'flex',
    gap: 5,
    padding: '2px 0',
    alignItems: 'center',
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.5)',
    display: 'inline-block',
    animation: 'qgDot 1.2s ease infinite',
  },
  inputArea: {
    padding: '12px 14px',
    borderTop: '1px solid rgba(255,255,255,0.07)',
    display: 'flex',
    gap: 10,
    alignItems: 'flex-end',
    background: 'rgba(0,0,0,0.2)',
  },
  input: {
    flex: 1,
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 12,
    padding: '10px 14px',
    color: 'white',
    fontSize: 14,
    resize: 'none',
    maxHeight: 100,
    lineHeight: 1.5,
  },
  sendBtn: {
    width: 42,
    height: 42,
    borderRadius: 12,
    background: 'linear-gradient(135deg, #eab308, #ca8a04)',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    transition: 'background 0.2s, transform 0.1s',
  },
  footer: {
    textAlign: 'center',
    fontSize: 11,
    color: 'rgba(255,255,255,0.3)',
    padding: '8px 16px',
    background: 'rgba(0,0,0,0.15)',
  },
};
